import plural from 'plural-ru'
import { RegisterOptions, Validate } from 'react-hook-form/dist/types/validator'

import { Moment } from 'moment'
import toArray from '@utils/array/toArray'
import getFileExtension from '@utils/string/getFileExtension/getFileExtension'
import humanFileSize from '@utils/string/humanFileSize'
import { toDate } from '@utils/date/toDate'
import * as regex from '../regex/regex'

/**
 * доступные расширения загружаемых файлов
 */
export type TFilesType = 'png' | 'jpg' | 'jpeg' | 'pdf' | 'webp' | 'gif'

export type TValidatorRules<T = unknown> = {
  [key in keyof T]?: TValidatorResult
}
export type TValidatorResult = Exclude<RegisterOptions, 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>

/**
 * Класс, формирует правила валидации для контроллеров
 * @example
 * new Validator.required().minLength(5).result
 */
export default class Validator {
  /**
   * Результирующий объект
   */
  protected __result__: TValidatorResult = {}
  protected __plural__ = {
    symbol: (value: number): string => `${value} ${plural(value, 'символ', 'символа', 'символов')}`,
  }
  public regex = { ...regex }

  get result(): TValidatorResult {
    return this.__result__
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  set result(_) {
    console.error(
      `You shouldn't change Validator.result directly. 
      Use Validator functions like .required(), .minLength(), etc... to set result`,
    )
  }

  /**
   * Функция хелпер для кастомных валидаторов
   * @param fnName название функции
   * @param callback
   * @protected
   */
  protected validateFn(fnName: string, callback: Validate): Validator {
    this.__result__ = {
      ...this.__result__,
      validate: {
        ...(this.__result__?.validate ?? {}),
        [fnName]: callback,
      },
    }
    return this
  }

  /**
   * Правило валидации "обязательно для заполнения"
   */
  required(errorMessage?: string): Validator {
    this.__result__ = {
      ...this.__result__,
      required: {
        value: true,
        message: errorMessage ?? 'Заполните поле',
      },
    }
    return this
  }

  /**
   * Булевые. Поле должно иметь значение перед отправкой, не являться null или undefined
   */
  bool(errorMessage?: string): Validator {
    return this.validateFn(
      'bool',
      (bool: boolean) => typeof bool === 'boolean' || (errorMessage ?? 'Поле должно быть заполнено'),
    )
  }

  /**
   * Правило валидации "минимальная длина поля"
   */
  minLength(value: number | string, errorMessage?: string): Validator {
    this.__result__ = {
      ...this.__result__,
      minLength: {
        value: +value,
        message: errorMessage ?? `Минимальная длина ${this.__plural__.symbol(+value)}`,
      },
    }
    return this
  }

  /**
   * Правило валидации "максимальная длина поля"
   */
  maxLength(value: number | string, errorMessage?: string): Validator {
    this.__result__ = {
      ...this.__result__,
      maxLength: {
        value: +value,
        message: errorMessage ?? `Максимальная длина ${this.__plural__.symbol(+value)}`,
      },
    }
    return this
  }

  /**
   * Правило валидации "Точная длина"
   */
  exactLength(value: number | string, errorMessage?: string): Validator {
    return this.validateFn(
      'exactLength',
      (string: string) =>
        string.length === +value || (errorMessage ?? `Длина поля должна быть ровно ${this.__plural__.symbol(+value)}`),
    )
  }

  /**
   * Правило валидации "Соответствие паролей"
   */
  passwordsEqual(value: string, errorMessage?: string): Validator {
    return this.validateFn(
      'passwordsEqual',
      (string: string) => string === value || (errorMessage ?? 'Пароли должны совпадать'),
    )
  }

  /**
   * Правила валидации "Длина поля между min и max, включая значения min и max"
   */
  length(min?: number | string, max?: number | string, errorMessage?: string): Validator {
    return this.validateFn('length', (string: string) => {
      if (!min && !max) return true
      if (min && !max) {
        return (
          string.length >= +min || (errorMessage ?? `Длина поля должна быть минимум ${this.__plural__.symbol(+min)}`)
        )
      }
      if (!min && max) {
        return (
          string.length <= +max || (errorMessage ?? `Длина поля должна быть максимум ${this.__plural__.symbol(+max)}`)
        )
      }
      if (min && max) {
        if (min === max) {
          return (
            string.length === +min || (errorMessage ?? `Длина поля должна быть ровно ${this.__plural__.symbol(+max)}`)
          )
        }

        return (
          (string.length >= +min && string.length <= +max) ||
          (errorMessage ?? `Длина поля должна быть ${+min}-${this.__plural__.symbol(+max)}`)
        )
      }
    })
  }

  /**
   * Правило валидации "только цифры"
   */
  onlyNumbers(errorMessage?: string): Validator {
    return this.validateFn(
      'onlyNumbers',
      (value: string) =>
        this.regex.REGEX_ONLY_NUMBERS.test(value) || (errorMessage ?? 'Неверно заполнено поле. Разрешены только цифры'),
    )
  }

  /**
   * Без пробелов
   */
  noWhitespace(errorMessage?: string): Validator {
    return this.validateFn(
      'noWhitespace',
      (value: string) => value.indexOf(' ') === -1 || (errorMessage ?? 'Неверно заполнено поле. Без пробелов'),
    )
  }

  /**
   * Правило валидации "E-Mail"
   */
  email(errorMessage?: string): Validator {
    return this.validateFn(
      'email',
      (value: string) =>
        this.regex.REGEX_EMAIL.test(value) ||
        (errorMessage ?? 'Неверно заполнено поле (E-Mail). Формат: example@mail.ru'),
    )
  }

  /**
   * Правило валидации "Разрешены только буквы (кириллица) и символы (дефис, тире)"
   */
  rusOnly(errorMessage?: string): Validator {
    return this.validateFn(
      'rusOnly',
      (value: string) =>
        this.regex.REGEX_RUS_ONLY.test(value) ||
        (errorMessage ?? 'Неверно заполнено поле. Разрешены только буквы (кириллица) и символы (дефис, тире)'),
    )
  }

  /**
   * Правило валидации "Ограничение промежтука для RangeDatePicker в годах"
   */
  dateRangeLimit(years: number, errorMessage?: string): Validator {
    return this.validateFn('dateRangeLimit', (value: [Moment, Moment]) => {
      return (
        value[1].year() - value[0].year() <= years ||
        (errorMessage ?? `Неверно заполнено поле. Разница между датами не должна превышать ${years} лет`)
      )
    })
  }

  /**
   * Правило валидации "Номер телефона, российский, мобильный"
   */
  phone(errorMessage?: string): Validator {
    return this.validateFn(
      'phone',
      (value: string) =>
        this.regex.REGEX_PHONE_RUS.test(value) ||
        (errorMessage ?? 'Неверно заполнено поле (номер телефона). Формат: +7 (999) 999-99-99'),
    )
  }

  /**
   * Правило валидации "Должны содержатьcя строчные буквы (a-z)"
   */
  atLeastOneLowercaseLatinic(errorMessage?: string): Validator {
    return this.validateFn(
      'atLeastOneLowercaseLatinic',
      (value: string) =>
        this.regex.REGEX_AT_LEAST_ONE_LOWERCASE_LATINIC_CHAR.test(value) ||
        (errorMessage ?? 'Должна содержатьcя как минимум одна строчная буква (a-z)'),
    )
  }

  /**
   * Правило валидации "Должны содержатьcя заглавные буквы (A-Z)"
   */
  atLeastOneUppercaseLatinic(errorMessage?: string): Validator {
    return this.validateFn(
      'atLeastOneUppercaseLatinic',
      (value: string) =>
        this.regex.REGEX_AT_LEAST_ONE_UPPERCASE_LATINIC_CHAR.test(value) ||
        (errorMessage ?? 'Должны содержатьcя заглавные буквы (A-Z)'),
    )
  }

  /**
   * Правило валидации "Дата не должна быть больше"
   */
  maxDate(value: string | Date, errorMessage?: string): Validator {
    return this.validateFn('maxDate', (date: Date) => {
      const dateOne = toDate(date)
      const dateTwo = toDate(value) || new Date()
      return (
        (dateOne && dateTwo && dateOne.getTime() <= dateTwo.getTime()) ||
        (errorMessage ?? `Дата не должна быть больше чем ${dateTwo.toLocaleDateString('ru-RU')}`)
      )
    })
  }

  /**
   * Правило валидации "Дата не должна быть меньше"
   */
  minDate(value: string | Date, errorMessage?: string): Validator {
    return this.validateFn('minDate', (date: Date) => {
      const dateOne = toDate(date)
      const dateTwo = toDate(value) || new Date()

      return (
        (dateOne && dateTwo && dateOne.getTime() >= dateTwo.getTime()) ||
        (errorMessage ?? `Дата не должна быть меньше чем ${dateTwo.toLocaleDateString('ru-RU')}`)
      )
    })
  }

  /**
   * Правило валидации "Должна содержаться хотя бы одна цифра"
   */
  atLeastOneNumber(errorMessage?: string): Validator {
    return this.validateFn(
      'atLeastOneNumber',
      (value: string) =>
        this.regex.REGEX_AT_LEAST_ONE_NUMBER.test(value) ||
        (errorMessage ?? 'Должна содержаться хотя бы одна цифра (0-9)'),
    )
  }

  /**
   * Правило валидации "Маска валидации, Regexp"
   */
  mask(regexp: RegExp, errorMessage?: string): Validator {
    return this.validateFn(
      'mask',
      (value: string) => regexp.test(value) || (errorMessage ?? `Должно соответствовать маске ${regexp.toString()}`),
    )
  }

  /**
   * Правило валидаци "Минимальное значение (число)"
   * @param minValue - минимальное значение
   * @param errorMessage - кастомное сообщение об ошибке
   */
  minNumber(minValue: number | string, errorMessage?: string): Validator {
    return this.validateFn(
      'minNumber',
      (value: string) => Number(value) >= +minValue || (errorMessage ?? `Должно быть больше ${minValue}`),
    )
  }

  /**
   * Правило валидаци "Максимальное значение (число)"
   * @param maxValue - Максимальное значение
   * @param errorMessage - кастомное сообщение об ошибке
   */
  maxNumber(maxValue: number | string, errorMessage?: string): Validator {
    return this.validateFn(
      'maxNumber',
      (value: string) => Number(value) < +maxValue || (errorMessage ?? `Должно быть меньше ${maxValue}`),
    )
  }

  /**
   * Правило валидации "Только буквы и цифры"
   * @param errorMessage - кастомное сообщение об ошибке
   */
  onlyNumbersAndLetters(errorMessage?: string): Validator {
    return this.validateFn(
      'onlyNumbersAndLetters',
      (value: string) =>
        this.regex.REGEX_ONLY_NUMBERS_AND_LETTERS.test(value) ||
        (errorMessage ?? 'Неверно заполнено поле. Разрешены только буквы и цифры'),
    )
  }

  /**
   * Правило валидации "Только буквы, цифры и знак вопроса"
   * @param errorMessage - кастомное сообщение об ошибке
   */
  onlyNumbersAndLettersAndQuestion(errorMessage?: string): Validator {
    return this.validateFn(
      'onlyNumbersAndLettersAndQuestion',
      (value: string) =>
        this.regex.REGEX_ONLY_NUMBERS_AND_LETTERS_AND_QUESTION.test(value) ||
        (errorMessage ?? 'Неверно заполнено поле. Разрешены только буквы, цифры и знак вопроса'),
    )
  }

  /**
   * Правило валидации разрешенные расширения для файлаплоадера
   * @param extensions - разрешенные расширения
   */
  filetype(extensions: TFilesType | Array<TFilesType>): Validator {
    const arExtensions = toArray(extensions).map((el) => el.toLowerCase().replace('.', ''))

    return this.validateFn('filetype', (files: FileList | Array<string> | string) => {
      if (!(files instanceof FileList)) return true
      return Array.from(files).filter(({ name }) => !arExtensions.includes(getFileExtension(name))).length > 0
        ? `Неправильное расширение файла. Разрешены: [${arExtensions.join(', ')}]`
        : true
    })
  }

  /**
   * Правило валидации для файлаплоадера, максимальный размер файла
   * @param sizeInBytes - размер в байтах
   * @param errorMessage - сообщение об ошибке
   */
  filesize(sizeInBytes: number | string, errorMessage?: string): Validator {
    return this.validateFn('filesize', (files: FileList | Array<string> | string) => {
      if (!(files instanceof FileList)) return true
      const errorText =
        errorMessage ?? `Слишком большой файл. Максимальный размер: ${humanFileSize(+sizeInBytes, true, 0)}`

      return Array.from(files).filter(({ size }) => size > sizeInBytes).length > 0 ? errorText : true
    })
  }

  /**
   * Правило валидации максиум файлов для файлаплоадера
   * @param max - максмальное коилчество файлов
   * @param errorMessage - сообщение об ошибке
   */
  maxFiles(max: number, errorMessage?: string): Validator {
    return this.validateFn('maxFiles', (files: Array<string> | FileList | string) => {
      const filesLength = typeof files === 'string' ? 1 : files.length
      if (filesLength > max) {
        return (
          errorMessage ??
          `Выбрано слишком много файлов. Можно загрузить не больше ${max} ${max === 1 ? 'файла' : 'файлов'}`
        )
      }
      return true
    })
  }

  /**
   * Правило валидации минимум файлов для файлаплоадера
   * @param min - минимальное коилчество файлов
   * @param errorMessage - сообщение об ошибке
   */
  minFiles(min: number, errorMessage?: string): Validator {
    return this.validateFn('minFiles', (files: Array<string> | FileList | string) => {
      const filesLength = typeof files === 'string' ? 1 : files.length
      if (filesLength < min) {
        return (
          errorMessage ??
          `Выбраны не все требуемые файлы. Нужно загрузить не меньше ${min} ${min === 1 ? 'файла' : 'файлов'}`
        )
      }
      return true
    })
  }

  /**
   * Правило валидации для свидетельства о рождении
   * @param errorMessage - сообщение об ошибке
   */
  birthdayCertificate(errorMessage?: string): Validator {
    return this.validateFn(
      'birthdayCertificate',
      (value: string) =>
        this.regex.REGEX_BIRTHDAY_CERTIFICATE.test(value) ||
        (errorMessage ??
          'Номер свидетельства о рождении должен состоять из латинских букв X, I, V до дефиса, двух русских букв после дефиса и шести цифр. Например: XIV-МЮ 123456'),
    )
  }
}

export type TValidatorMethods = keyof Omit<Validator, 'result' | 'regex'>
